import {
  EventsUpload,
  FeedbackUpload,
  MAX_EVENTS_PER_BATCH,
  TelemetryEvent,
} from "./wire";

/**
 * The client half of the demo's telemetry (issue #248): a queue of
 * events that batches in memory and leaves the page as one beacon.
 *
 * What goes in it:
 *
 * - `track(name, props)` — a discrete fact with a timestamp: a milestone
 *   reached, a session begun, an error thrown. Each is one row.
 * - `count(key)` — a stuck signal that would be noise as rows (every
 *   Escape press, every key that did nothing at a machine). Counters
 *   accumulate and fold into one `counters` event whenever the batch
 *   leaves or the position moves, so a counters event always belongs
 *   to one stretch of the spine.
 * - `beginView(id, props)` — the player went somewhere: the store, the
 *   bench, the manual. The closer it returns records one `view` event
 *   with the seconds spent there. A session ending closes what's open.
 *
 * Every event is stamped twice over (wire.ts): with the wall clock, and
 * with the play clock — seconds this session the game was neither
 * paused nor hidden in a background tab, kept by `setPaused` and
 * `setHidden` — and with the position on the spine (`moveTo`), the
 * furthest milestone reached, which the recorder keeps current.
 *
 * When it leaves: `flush()` hands everything since the last flush to the
 * transport in one batch; `end()` marks the session over first. The
 * shell wires `end` to `pagehide` and `flush` to the tab going hidden
 * (telemetry.ts) — the same two hooks the autosave uses — so a session
 * costs the database one wake in the common case and one per
 * backgrounding on a phone that may never fire `pagehide`. There is no
 * timer: a flush that nothing preceded sends nothing.
 *
 * Errors after the twentieth are dropped, so a throw in a render loop
 * cannot fill the batch with one line repeated.
 *
 * The transport is injected — a `sendBeacon` in the shell, a console
 * line in dev builds, a spy in tests. Feedback uploads go through the
 * transport's `post`, which waits for an answer, because the player is
 * looking at a button that should say whether the note arrived.
 */

export interface TelemetryTransport {
  /** Fire-and-forget; false when the browser refused the payload. */
  beacon(body: string): boolean;
  /** A request the caller waits on; resolves to whether it succeeded. */
  post(body: string): Promise<boolean>;
}

export interface TelemetryClientOptions {
  transport: TelemetryTransport;
  playerId: string;
  sessionId: string;
  build: string;
  /** Wall-clock milliseconds; injectable for tests. */
  now?: () => number;
  /** Whether the page is hidden at construction. */
  hidden?: boolean;
}

/** Error events kept per session before the rest are dropped. */
export const MAX_ERRORS_PER_SESSION = 20;

export class TelemetryClient {
  private readonly transport: TelemetryTransport;
  readonly playerId: string;
  readonly sessionId: string;
  readonly build: string;
  private readonly now: () => number;

  private events: TelemetryEvent[] = [];
  private counters = new Map<string, number>();
  private readonly startedAt: number;
  private ended = false;
  private errors = 0;

  private position: string | undefined;

  // The play clock: seconds banked while playing, plus the stretch
  // since it last started if it is running now.
  private paused = false;
  private hidden: boolean;
  private playBanked = 0;
  private playSince: number | null = null;

  private readonly openViews = new Set<() => void>();

  constructor(options: TelemetryClientOptions) {
    this.transport = options.transport;
    this.playerId = options.playerId;
    this.sessionId = options.sessionId;
    this.build = options.build;
    this.now = options.now ?? (() => Date.now());
    this.startedAt = this.now();
    this.hidden = options.hidden ?? false;
    this.settlePlayClock();
  }

  /** Where on the spine the player is, as the last `moveTo` said. */
  get at(): string | undefined {
    return this.position;
  }

  /** Seconds of play so far: unpaused and visible. */
  play(): number {
    const running = this.playSince === null ? 0 : this.now() - this.playSince;
    return Math.round((this.playBanked + running) / 100) / 10;
  }

  setPaused(paused: boolean): void {
    if (this.paused === paused) return;
    this.paused = paused;
    this.settlePlayClock();
  }

  setHidden(hidden: boolean): void {
    if (this.hidden === hidden) return;
    this.hidden = hidden;
    this.settlePlayClock();
  }

  private settlePlayClock(): void {
    const playing = !this.paused && !this.hidden;
    if (playing && this.playSince === null) {
      this.playSince = this.now();
    } else if (!playing && this.playSince !== null) {
      this.playBanked += this.now() - this.playSince;
      this.playSince = null;
    }
  }

  /**
   * The player reached a further point on the spine. What was counted
   * up to now belongs to the stretch just finished, so the counters
   * fold first, stamped with the old position.
   */
  moveTo(at: string): void {
    if (at === this.position) return;
    this.foldCounters();
    this.position = at;
  }

  /** Record one fact. Dropped when the batch is full. */
  track(name: string, props?: Record<string, unknown>): void {
    if (this.events.length >= MAX_EVENTS_PER_BATCH) return;
    if (name === "error") {
      if (this.errors >= MAX_ERRORS_PER_SESSION) return;
      this.errors += 1;
    }
    this.events.push({
      name,
      ts: this.now(),
      play: this.play(),
      ...(this.position !== undefined ? { at: this.position } : {}),
      ...(props ? { props } : {}),
    });
  }

  /** Tally one stuck signal; folded into a `counters` event later. */
  count(key: string): void {
    this.counters.set(key, (this.counters.get(key) ?? 0) + 1);
  }

  /**
   * The player went somewhere. Returns the closer, which records the
   * visit as one `view` event with its length in wall seconds. Closing
   * twice is harmless; a session ending closes everything still open.
   */
  beginView(id: string, props?: Record<string, unknown>): () => void {
    const since = this.now();
    const close = () => {
      if (!this.openViews.delete(close)) return;
      this.track("view", {
        id,
        ...props,
        seconds: Math.round((this.now() - since) / 1000),
      });
    };
    this.openViews.add(close);
    return close;
  }

  /** Events and counters waiting for the next flush. */
  pending(): number {
    return this.events.length + (this.counters.size > 0 ? 1 : 0);
  }

  private foldCounters(): void {
    if (this.counters.size === 0) return;
    const tallies = Object.fromEntries(this.counters);
    this.counters.clear();
    this.track("counters", tallies);
  }

  /**
   * Send everything since the last flush as one batch. Returns whether
   * anything was sent. The queue is cleared either way: a beacon the
   * browser refuses (too large for its queue) is not one that gets
   * smaller by waiting.
   */
  flush(): boolean {
    this.foldCounters();
    if (this.events.length === 0) return false;
    const batch: EventsUpload = {
      kind: "events",
      v: 1,
      sessionId: this.sessionId,
      playerId: this.playerId,
      build: this.build,
      events: this.events,
    };
    this.events = [];
    return this.transport.beacon(JSON.stringify(batch));
  }

  /** The session is over: close its views, note how long it ran, then flush. Once only. */
  end(): void {
    if (!this.ended) {
      this.ended = true;
      for (const close of [...this.openViews]) close();
      this.foldCounters();
      this.track("session_end", {
        seconds: Math.round((this.now() - this.startedAt) / 1000),
      });
    }
    this.flush();
  }

  /**
   * A note from the player with their save attached, sent on its own
   * and waited for. Resolves to whether the note arrived.
   */
  sendFeedback(note: string, save: unknown | null): Promise<boolean> {
    const upload: FeedbackUpload = {
      kind: "feedback",
      v: 1,
      sessionId: this.sessionId,
      playerId: this.playerId,
      build: this.build,
      ...(this.position !== undefined ? { at: this.position } : {}),
      note,
      save: save as FeedbackUpload["save"],
    };
    return this.transport.post(JSON.stringify(upload));
  }
}
